import * as BABYLON from 'babylonjs';
import * as BABYLONGUI from 'babylonjs-gui';
import PanoEvents from '../Enumerations/ViewerEnums';
import PanoConstants from '../constants/ViewerConstants';
import PanoViewer from './PanoViewer';
import PanoDome from './PanoDome';
import DepthMap from './DepthMap';
import XmlLoader2 from './XmlLoader2';
import CustomInputText from '../components/CustomInputText';
import * as Inovx from '@shared';
import BoxManager from './BoxManager';
import Draco3d from 'draco3d';
import ViewerStore from '../../stores/ViewerStore';
import { BlackAndWhiteScanPositionColor, isBlackAndWhiteScanPosition, isScanPositionView, toHeadingDegrees } from '../../helpers/viewMedia';
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

interface PanoSceneOptions {
    showTripods?: boolean,
    showScanPositions?: boolean,
    showColorScanPositions?: boolean,
    showBlackAndWhiteScanPositions?: boolean,
    showImagePositions?: boolean,
    scanSphereColor?: string,
    imageSphereColor?: string,
    tagVolumeColor?: string,
    measurementColor?: string
}

interface ILabel {
    labelControl: BABYLONGUI.Container,
    attachedMesh: BABYLON.Mesh,
    line?: BABYLON.LinesMesh,
    lineData?: { color: string },
    annotationData: IAnnotationDisplayItem
}

class PanoSceneManager {
    private _preColorizeCache: Map<string, {
        color: string,
        background: string,
        textMap: Map<BABYLONGUI.TextBlock, string>
    }> = new Map();

    private _activeAppearance: Map<string, {
        backgroundHex: string,
        textColor: string
    }> = new Map();

    private readonly DefaultScanSphereColor = "#00FFFB";
    private readonly DefaultImageSphereColor = "#F59300";
    private readonly HighlightedSphereColor = "#FF0000";
    private readonly SelectedTagColor = "#00c6cd";       // UPDATED
    private readonly SelectedFontColor = "#ece9e9";      // ADDED
    private readonly OccludedTagAlpha = 0.55;
    private readonly OccludedTagLineAlpha = "99";

    private _onKeyDown: (e: KeyboardEvent) => void;
    private _container: HTMLDivElement;
    private _selectedView: IView360Detail;
    private _views: IView360Detail[];
    private _selectedLabel: ILabel = null;
    private _preSelectAnnControlColor: string = null;
    private _preSelectAnnControlBackground: string = null; // ADDED
    private _annotationsReadOnly: boolean = false;
    private _rotationValid: boolean = true;

    private _initialized: boolean = false;
    private _switchingViews: boolean = false;
    private _animationDone: boolean = false;

    private _scanMaterial: BABYLON.StandardMaterial;
    private _scanHighlightMaterial: BABYLON.StandardMaterial;
    private _blackAndWhiteScanMaterial: BABYLON.StandardMaterial;
    private _blackAndWhiteScanHighlightMaterial: BABYLON.StandardMaterial;
    private _image360Material: BABYLON.StandardMaterial;
    private _image360HighlightMaterial: BABYLON.StandardMaterial;

    private _tripods: BABYLON.Mesh[];
    private _tripodsByViewId: Map<string, BABYLON.Mesh> = new Map();
    private _highlightedTripod: BABYLON.Mesh = null;

    private _viewer: PanoViewer;

    private _depthMap: DepthMap;
    private _depthMapInLoad: DepthMap;

    private _options: PanoSceneOptions;

    public events: EventTarget;

    private _guiTexture: BABYLONGUI.AdvancedDynamicTexture;
    private _glowLayer: BABYLON.GlowLayer;

    private _labels: ILabel[] = [];

    private _occludedAnnotations: string[] = [];

    // Cache of original TextBlock colors by annotation id (ES5-friendly usage)
    private _labelTextColorCache: Map<string, Map<BABYLONGUI.TextBlock, string>> = new Map(); // ADDED

    private get scene() { return this._viewer.scene; }
    private get camera() { return this._viewer.camera; }
    private get boxManager() { return this._viewer.boxManager; }

    public get viewer() { return this._viewer; }
    public get isBusy() { return !this._initialized || this._switchingViews; }

    public get isDepthMapLoaded() { return (this._depthMap) ? true : false }

    constructor(div: HTMLDivElement, selectedView: IView360Detail, views: IView360Detail[], options?: PanoSceneOptions) {

        this._container = div;
        this._views = views;
        this._selectedView = selectedView;
        this._options = {
            showTripods: options?.showTripods ?? false,
            showScanPositions: options?.showScanPositions ?? true,
            showColorScanPositions: options?.showColorScanPositions ?? true,
            showBlackAndWhiteScanPositions: options?.showBlackAndWhiteScanPositions ?? true,
            showImagePositions: options?.showImagePositions ?? true,
            scanSphereColor: options?.scanSphereColor ?? this.DefaultScanSphereColor,
            imageSphereColor: options?.imageSphereColor ?? this.DefaultImageSphereColor,
            tagVolumeColor: options?.tagVolumeColor,
            measurementColor: options?.measurementColor
        }

        this.events = new EventTarget();

        this.setMeasurements(ViewerStore.getMeasurements());
    }

    private _getPanoViewerOptions(selectedView: IView360Detail) {
        const isScanPosition = isScanPositionView(selectedView);
        const headingDegrees = toHeadingDegrees(selectedView.Rotation);

        return {
            measurementColor: this._options.measurementColor,
            imageRotation: isScanPosition || headingDegrees == null ? undefined : Math.PI / 2 + headingDegrees * (Math.PI / 180),
            initialDirection: isScanPosition ? undefined : new BABYLON.Vector3(0, 0, 1)
        };
    }

    public dispose() {

        if (this._onKeyDown) {
            window.removeEventListener('keydown', this._onKeyDown);
            this._onKeyDown = null;
        }

        this._glowLayer?.dispose();
        this._glowLayer = null;

        this._guiTexture?.dispose();
        this._guiTexture = null;

        this._cleanupTripods(true);
        this._clearAnnotations();
        this._clearDepthMap();

        this._viewer?.dispose();
    }

    private _clearDepthMap() {
        this._occludedAnnotations = [];

        this._depthMap?.dispose();
        this._depthMap = null;

        this._viewer?.setDepthMap(null);
    }

    private _cleanupTripods(disposeMaterials: boolean) {
        if (this._tripods) {
            this._tripods.forEach(pod => {
                this._glowLayer?.removeIncludedOnlyMesh(pod);
                pod.dispose();
            });
        }
        this._tripods = [];
        this._tripodsByViewId.clear();
        this._highlightedTripod = null;

        if (disposeMaterials) {
            this._scanHighlightMaterial?.dispose();
            this._scanHighlightMaterial = null;
            this._scanMaterial?.dispose();
            this._scanMaterial = null;
            this._blackAndWhiteScanHighlightMaterial?.dispose();
            this._blackAndWhiteScanHighlightMaterial = null;
            this._blackAndWhiteScanMaterial?.dispose();
            this._blackAndWhiteScanMaterial = null;
            this._image360HighlightMaterial?.dispose();
            this._image360HighlightMaterial = null;
            this._image360Material?.dispose();
            this._image360Material = null;
        }
    }

    private _clearAnnotations() {
        this.boxManager.clearBox();
        this._labels.forEach(labelData => {
            this._deleteLabel(labelData);
        });
        this._labels = [];
    }

    private _selectLabel(label: ILabel) {
        if (label == this._selectedLabel)
            return;

        // Clear the previous selection
        if (this._selectedLabel) {
            // Restore container colors
            this._selectedLabel.labelControl.color = this._preSelectAnnControlColor;
            (this._selectedLabel.labelControl as any).background = this._preSelectAnnControlBackground;
            this._preSelectAnnControlColor = null;
            this._preSelectAnnControlBackground = null;

            // Restore label text colors
            this._restoreTextColors(this._selectedLabel.annotationData.ID);

            if (this._selectedLabel.lineData) {
                // Rebuild line color (keep original alpha suffix if present)
                var alphaSuffix = this._selectedLabel.lineData.color && this._selectedLabel.lineData.color.length >= 9
                    ? this._selectedLabel.lineData.color.substring(7, 9)
                    : "";
                this._selectedLabel.lineData.color = this._selectedLabel.labelControl.color + alphaSuffix;
            }

            if (this._isBoxLabel(this._selectedLabel)) {
                this.boxManager.clearBox();
            }
        }

        // Set the new selection
        if (label) {
            this._preSelectAnnControlColor = (label.labelControl as any).originalColor;
            this._preSelectAnnControlBackground = (label.labelControl as any).originalBackground;

            // Highlight container: border/foreground + background
            label.labelControl.color = this.SelectedTagColor;
            (label.labelControl as any).background = this.SelectedTagColor;

            if (label.lineData) {
                var alphaSuffixNew = label.lineData.color && label.lineData.color.length >= 9
                    ? label.lineData.color.substring(7, 9)
                    : "";
                label.lineData.color = label.labelControl.color + alphaSuffixNew;
            }

            // Update all inner text to selected font color
            this._setTextColorRecursive(label.labelControl, this.SelectedFontColor, label.annotationData.ID);

            if (this._isBoxLabel(label)) {
                const boxData = this._getLabelBoxDimensions(label);
                this.boxManager.createBox({ realWorldPos: boxData.boxPosition, xDim: boxData.boxSize.x, yDim: boxData.boxSize.y, zDim: boxData.boxSize.z });
            }
        }

        this._selectedLabel = label;
    }

    private _rgbaNumberToHexRRGGBBAA(color: number): string {
        const hex = (color >>> 0).toString(16).padStart(8, '0');
        return '#' + hex;
    }

    private _getReadableTextColor(background: string): string {
        const babylonColor = BABYLON.Color4.FromHexString(background);
        const bg = {
            R: Math.round(babylonColor.r * 255),
            G: Math.round(babylonColor.g * 255),
            B: Math.round(babylonColor.b * 255)
        };
        const luminance = (0.299 * bg.R + 0.587 * bg.G + 0.114 * bg.B) / 255;
        return luminance > 0.5 ? '#000000' : '#FFFFFF';
    }

    private _isSelectedLabel(label: ILabel): boolean {
        return !!label && !!this._selectedLabel && this._selectedLabel.annotationData.ID === label.annotationData.ID;
    }

    public colorizeAnnotations(annotationIds: string[], backgroundColor: number): void {

        if (backgroundColor == null)
            return;

        for (const annId of annotationIds) {
            const label = this._labels.find(l => l.annotationData.ID === annId);
            if (!label) continue;

            const isSelected = this._isSelectedLabel(label);

            let textMap = this._labelTextColorCache.get(annId);
            if (!textMap) {
                textMap = new Map<BABYLONGUI.TextBlock, string>();
                this._labelTextColorCache.set(annId, textMap);
            }

            if (!this._preColorizeCache.has(annId)) {
                this._preColorizeCache.set(annId, {
                    color: (label.labelControl as any).originalColor ?? label.labelControl.color,
                    background: (label.labelControl as any).originalBackground ?? (label.labelControl as any).background,
                    textMap: new Map(textMap)
                });
            }

            const originalEntry = this._preColorizeCache.get(annId);

            const resolvedBackgroundHex =
                backgroundColor == null
                    ? (originalEntry?.background ?? (label.labelControl as any).originalBackground ?? (label.labelControl as any).background)
                    : this._rgbaNumberToHexRRGGBBAA(backgroundColor);

            const textColor = this._getReadableTextColor(resolvedBackgroundHex);

            this._activeAppearance.set(annId, {
                backgroundHex: resolvedBackgroundHex,
                textColor
            });

            label.labelControl.color = resolvedBackgroundHex;
            (label.labelControl as any).background = resolvedBackgroundHex;

            this._setTextColorRecursive(label.labelControl, textColor, annId);

            if (label.lineData) {
                const alphaSuffix = label.lineData.color && label.lineData.color.length >= 9
                    ? label.lineData.color.substring(7, 9)
                    : '';
                label.lineData.color = resolvedBackgroundHex + alphaSuffix;
            }

            if (this._isBoxLabel(label) && this._isSelectedLabel(label)) {
                const boxData = this._getLabelBoxDimensions(label);
                if (boxData?.boxPosition && boxData?.boxSize) {
                    this.boxManager.createBox({
                        realWorldPos: boxData.boxPosition,
                        xDim: boxData.boxSize.x,
                        yDim: boxData.boxSize.y,
                        zDim: boxData.boxSize.z
                    });
                    this.boxManager.setBoxColor(resolvedBackgroundHex);
                }
            }

            if (isSelected) {
                this._applySelectedAppearance(label);
            }
        }
    }

    private _applySelectedAppearance(label: ILabel): void {
        if (!label) return;

        this._preSelectAnnControlColor = (label.labelControl as any).originalColor ?? label.labelControl.color;
        this._preSelectAnnControlBackground = (label.labelControl as any).originalBackground ?? (label.labelControl as any).background;

        label.labelControl.color = this.SelectedTagColor;
        (label.labelControl as any).background = this.SelectedTagColor;

        if (label.lineData) {
            const alphaSuffix = label.lineData.color && label.lineData.color.length >= 9
                ? label.lineData.color.substring(7, 9)
                : '';
            label.lineData.color = this.SelectedTagColor + alphaSuffix;
        }

        this._setTextColorRecursive(label.labelControl, this.SelectedFontColor, label.annotationData.ID);
    }

    public resetAllAnnotationColorization(): void {

        const defaultBoxColor = this._options.tagVolumeColor;
        this.boxManager.setBoxColor(defaultBoxColor);

        this._preColorizeCache.forEach((colors, annId) => {
            const label = this._labels.find(l => l.annotationData.ID === annId);
            if (!label) return;

            label.labelControl.color = colors.color;
            (label.labelControl as any).background = colors.background;

            this._restoreTextColors(annId);

            if (label.lineData) {
                const alphaSuffix = label.lineData.color && label.lineData.color.length >= 9
                    ? label.lineData.color.substring(7, 9)
                    : '';
                label.lineData.color = colors.color + alphaSuffix;
            }

            this._activeAppearance.delete(annId);

            if (this._isBoxLabel(label) && this._isSelectedLabel(label)) {
                const restoredBoxColor = this._options.tagVolumeColor;
                if (restoredBoxColor) {
                    this.boxManager.setBoxColor(restoredBoxColor);
                }
            }

            if (this._isSelectedLabel(label)) {
                this._applySelectedAppearance(label);
            }
        });

        this._preColorizeCache.clear();
    }

    private _isBoxLabel(label: ILabel) {
        return label.annotationData.TargetBoxMin;
    }

    private _getLabelBoxDimensions(label: ILabel) {
        const annotation = label.annotationData;

        let boxPosition: BABYLON.Vector3;
        let boxSize: BABYLON.Vector3;
        if (annotation.TargetBoxMin) {
            boxPosition = new BABYLON.Vector3((annotation.TargetBoxMin.X + annotation.TargetBoxMax.X) / 2, (annotation.TargetBoxMin.Y + annotation.TargetBoxMax.Y) / 2, (annotation.TargetBoxMin.Z + annotation.TargetBoxMax.Z) / 2);
            boxSize = new BABYLON.Vector3(annotation.TargetBoxMax.X - annotation.TargetBoxMin.X, annotation.TargetBoxMax.Y - annotation.TargetBoxMin.Y, annotation.TargetBoxMax.Z - annotation.TargetBoxMin.Z);
        }
        return { boxPosition, boxSize };
    }

    private _createAnnotation(annotation: IAnnotationDisplayItem, tagDisplayXml: string) {

        const xmlLoader = new XmlLoader2(annotation);
        xmlLoader.loadLayoutFromString(tagDisplayXml, null);

        const annControl: BABYLONGUI.Container = xmlLoader.getNodeById("root");

        const baseUrl = UrlUtilities.getBaseApplicationUrl();
        this._updateSourceUrls(annControl, baseUrl);

        this._guiTexture.addControl(annControl);

        let boxPosition: BABYLON.Vector3;
        let boxDimensions: BABYLON.Vector3;

        if (annotation.TargetBoxMin) {
            boxPosition = new BABYLON.Vector3((annotation.TargetBoxMin.X + annotation.TargetBoxMax.X) / 2, (annotation.TargetBoxMin.Y + annotation.TargetBoxMax.Y) / 2, (annotation.TargetBoxMin.Z + annotation.TargetBoxMax.Z) / 2);
            boxDimensions = new BABYLON.Vector3(annotation.TargetBoxMax.X - annotation.TargetBoxMin.X, annotation.TargetBoxMax.Y - annotation.TargetBoxMin.Y, annotation.TargetBoxMax.Z - annotation.TargetBoxMin.Z);
        }

        if (annotation.Target3D) {
            const index = this._labels.length;

            const attachmentSphere = BABYLON.MeshBuilder.CreateSphere('annSphere' + index, { diameter: 0.1 }, this.scene);

            let domePosition: BABYLON.Vector3;
            if (boxPosition) {
                domePosition = this._projectVectorToDome(boxPosition);
            }
            else {
                domePosition = this._projectVectorToDome(this._getVectorFromViewData(annotation.Target3D));
            }

            attachmentSphere.position = domePosition;

            let lineData = { color: annControl.color };
            if (annotation.Target2D) {
                annControl.linkOffsetX = annotation.Target2D.X;
                annControl.linkOffsetY = annotation.Target2D.Y;
            }
            else {
                // Annotations without an offset (usually icons) do not need a connector line.
                if (annControl.linkOffsetXInPixels == 0 && annControl.linkOffsetYInPixels == 0) {
                    lineData = null;
                }
            }

            // Keep originals (pre-conversion)
            (annControl as any).originalColor = annControl.color;
            (annControl as any).originalBackground = annControl.background;

            const isOccluded = this._occludedAnnotations.includes(annotation.ID);
            if (isOccluded) {
                annControl.alpha = this.OccludedTagAlpha;

                if (lineData)
                    lineData.color += this.OccludedTagLineAlpha;
            }
            else {
                // Swap the alpha to only apply to the background color
                annControl.color = this.hexToRgb(annControl.color, 1.0);
                annControl.background = this.hexToRgb(annControl.background, annControl.alpha);
                annControl.alpha = 1.0;
            }

            annControl.linkWithMesh(attachmentSphere);

            const annotationDataCopy = { ...annotation };
            const labelData: ILabel = {
                labelControl: annControl,
                attachedMesh: attachmentSphere,
                line: null,
                lineData: lineData,
                annotationData: annotationDataCopy
            };
            return labelData;
        }
    }

    private hexToRgb(hex: string, alpha: number) {
        var hexRGB = hex.replace(/^#/, '');

        let bigint = parseInt(hexRGB, 16);
        let r = (bigint >> 16) & 255;
        let g = (bigint >> 8) & 255;
        let b = bigint & 255;

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    private _updateLabelPosition(label: ILabel, position: BABYLON.Vector3) {
        if (label?.attachedMesh) {
            label.attachedMesh.position = this._projectVectorToDome(position);
        }
    }

    private _updateSourceUrls(control: BABYLONGUI.Control, baseUrl: string) {
        if (control instanceof BABYLONGUI.Image && control.source) {
            if (!control.source.startsWith('http://') && !control.source.startsWith('https://')) {
                control.source = `${baseUrl}/${control.source}`;
            }
        }

        if ('children' in control) {
            for (const child of (control as any).children) {
                this._updateSourceUrls(child, baseUrl);
            }
        }
    }

    private _project2DPointTo3D = (x: number, y: number) => {
        let screenCoords = new BABYLON.Vector3(x, y, 0);
        let worldCoords = BABYLON.Vector3.Unproject(screenCoords,
            this.scene.getEngine().getRenderWidth(),
            this.scene.getEngine().getRenderHeight(),
            BABYLON.Matrix.Identity(),
            this.camera.getViewMatrix(),
            this.camera.getProjectionMatrix());

        return worldCoords;
    }

    private _projectVectorToDome(vector: BABYLON.Vector3) {
        const cameraPosition = this._getVectorFromViewData(this._selectedView.Position);
        const cameraRotMatrix = this.camera.getImageRotationMatrix();

        const viewDirVec = vector.subtract(cameraPosition);

        let viewDir = viewDirVec.normalizeToNew();
        viewDir = BABYLON.Vector3.TransformCoordinates(viewDir, cameraRotMatrix);

        const domeViewPos = viewDir.scale(PanoDome.DomeRadius);

        return domeViewPos;
    }

    private _getViewFromSphereMesh(sphere: BABYLON.AbstractMesh) {
        return this._views.find(view => view.ID == sphere.name);
    }

    private _initializeTripods(disposeMaterials: boolean = false) {

        this._cleanupTripods(disposeMaterials);

        if (!this._options.showTripods) {
            return;
        }

        if (!this.isDepthMapLoaded) {
            return;
        }

        if (!this._scanMaterial) {
            const tripodMaterial = new BABYLON.StandardMaterial("scanMaterial", this.scene);
            tripodMaterial.alpha = 0.3;
            tripodMaterial.diffuseColor = BABYLON.Color3.FromHexString(this._options.scanSphereColor);

            const tripodGlowMaterial = tripodMaterial.clone("scanMaterial_glow");
            tripodGlowMaterial.diffuseColor = BABYLON.Color3.FromHexString(this.HighlightedSphereColor);
            tripodGlowMaterial.emissiveColor = BABYLON.Color3.FromHexString(this.HighlightedSphereColor);
            tripodGlowMaterial.alpha = 0.75;

            this._scanMaterial = tripodMaterial;
            this._scanHighlightMaterial = tripodGlowMaterial;
        }

        if (!this._image360Material) {
            const tripodMaterial = new BABYLON.StandardMaterial("image360Material", this.scene);
            tripodMaterial.alpha = 0.3;
            tripodMaterial.diffuseColor = BABYLON.Color3.FromHexString(this._options.imageSphereColor);

            const tripodGlowMaterial = tripodMaterial.clone("image360Material_glow");
            tripodGlowMaterial.diffuseColor = BABYLON.Color3.FromHexString(this.HighlightedSphereColor);
            tripodGlowMaterial.emissiveColor = BABYLON.Color3.FromHexString(this.HighlightedSphereColor);
            tripodGlowMaterial.alpha = 0.75;

            this._image360Material = tripodMaterial;
            this._image360HighlightMaterial = tripodGlowMaterial;
        }

        if (!this._blackAndWhiteScanMaterial) {
            const tripodMaterial = new BABYLON.StandardMaterial("blackAndWhiteScanMaterial", this.scene);
            tripodMaterial.alpha = 0.3;
            tripodMaterial.diffuseColor = BABYLON.Color3.FromHexString(BlackAndWhiteScanPositionColor);

            const tripodGlowMaterial = tripodMaterial.clone("blackAndWhiteScanMaterial_glow");
            tripodGlowMaterial.diffuseColor = BABYLON.Color3.FromHexString(this.HighlightedSphereColor);
            tripodGlowMaterial.emissiveColor = BABYLON.Color3.FromHexString(this.HighlightedSphereColor);
            tripodGlowMaterial.alpha = 0.75;

            this._blackAndWhiteScanMaterial = tripodMaterial;
            this._blackAndWhiteScanHighlightMaterial = tripodGlowMaterial;
        }

        if (this.camera == null) {
            // This can occur when tripods are initializing after the pano scene is abandonded via a viewer change.
            return;
        }
        const cameraPosition = this._getVectorFromViewData(this._selectedView.Position);
        const cameraRotMatrix = this.camera?.getImageRotationMatrix();

        this._views.map((view, index) => {
            if (view != this._selectedView) {
                const viewPosition = this._getVectorFromViewData(view.Position);

                const validRotation = isScanPositionView(view);
                const isBlackAndWhite = validRotation && isBlackAndWhiteScanPosition(view);
                const displayPositionType = validRotation
                    ? this._options.showScanPositions && (isBlackAndWhite
                        ? this._options.showBlackAndWhiteScanPositions
                        : this._options.showColorScanPositions)
                    : this._options.showImagePositions;
                const isWithinDepthMapRange = this._isWithinDepthMapRange(viewPosition, cameraPosition);

                if (displayPositionType && isWithinDepthMapRange && !this._depthMap.isPositionOccluded(viewPosition, cameraPosition)) {
                    const viewDirVec = viewPosition.subtract(cameraPosition);
                    const viewDirLen = viewDirVec.length();

                    let viewDir = viewDirVec.normalizeToNew();
                    viewDir = BABYLON.Vector3.TransformCoordinates(viewDir, cameraRotMatrix);

                    const domeViewPos = viewDir.scale(PanoDome.DomeRadius);
                    const sphereDiameter = this._calculateSphereDiameter(viewDirLen);

                    const sphere = BABYLON.MeshBuilder.CreateSphere(view.ID, { diameter: sphereDiameter }, this.scene);
                    sphere.material = validRotation
                        ? (isBlackAndWhite ? this._blackAndWhiteScanMaterial : this._scanMaterial)
                        : this._image360Material;
                    sphere.position = domeViewPos;
                    sphere.renderingGroupId = PanoConstants.RenderingGroups.ObjectsRenderingGroupId;

                    sphere.actionManager = new BABYLON.ActionManager(this.scene);

                    // Change cursor to 'hand' when hovering over the sphere
                    sphere.actionManager.registerAction(new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPointerOverTrigger, (e) => {
                        if (!this.isBusy) {
                            this.scene.getEngine().getRenderingCanvas().style.cursor = 'pointer';

                            this._highlightTripod(e.source as BABYLON.Mesh);

                            const pickedView = this._getViewFromSphereMesh(sphere);
                            this.events.dispatchEvent(new CustomEvent(PanoEvents.Viewer.VIEW_HIGHLIGHTED, {
                                detail: { view: pickedView }
                            }));
                        }
                    }));

                    // Revert cursor when no longer hovering over the sphere
                    sphere.actionManager.registerAction(new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPointerOutTrigger, (e) => {
                        if (!this.isBusy) {
                            this.scene.getEngine().getRenderingCanvas().style.cursor = '';

                            this._unhighlightTripod(e.source as BABYLON.Mesh);

                            this.events.dispatchEvent(new CustomEvent(PanoEvents.Viewer.VIEW_HIGHLIGHTED, {
                                detail: { view: null }
                            }));
                        }
                    }));

                    this._tripods.push(sphere);
                    this._tripodsByViewId.set(view.ID, sphere);
                }
            }
        });
    }

    private _onAfterCameraRender(camera: BABYLON.Camera) {
        this._labels.forEach((labelData, index) => {
            if (labelData.attachedMesh && labelData.lineData) {
                const attachmentPoint3D = labelData.attachedMesh.position;
                const labelPoint2D = new BABYLON.Vector2(labelData.labelControl.leftInPixels + labelData.labelControl.widthInPixels / 2, labelData.labelControl.topInPixels);
                const labelPoint2Din3D = this._project2DPointTo3D(labelPoint2D.x, labelPoint2D.y);

                labelData.line = BABYLON.MeshBuilder.CreateLines("annLine" + index, {
                    points: [attachmentPoint3D, labelPoint2Din3D],
                    updatable: true,
                    instance: labelData.line,

                    colors: [BABYLON.Color4.FromHexString(labelData.lineData.color), BABYLON.Color4.FromHexString(labelData.lineData.color)]
                }, this.scene);
                labelData.line.isPickable = false;
            }
        });
    }

    private _getVectorFromViewData(srcVector: IVector3D) {
        return new BABYLON.Vector3(srcVector.X, srcVector.Y, srcVector.Z);
    }

    private _isWithinDepthMapRange(position: BABYLON.Vector3, cameraPosition: BABYLON.Vector3) {
        // Radius contains the depth map's MaximumDepth in its scaled integer units.
        const maximumDepth = this._selectedView.Radius;
        const scaleFactor = this._selectedView.ScaleFactor;

        // Preserve existing behavior for legacy or incomplete scan metadata.
        if (!Number.isFinite(maximumDepth) || maximumDepth <= 0 ||
            !Number.isFinite(scaleFactor) || scaleFactor <= 0) {
            return true;
        }

        return BABYLON.Vector3.Distance(position, cameraPosition) <= maximumDepth / scaleFactor;
    }

    private _calculateSphereDiameter(distance: number): number {
        const minDistance = 0;
        const maxDistance = 35;
        const minDiameter = 10;
        const maxDiameter = 45;

        let diameter = 0;

        if (distance <= minDistance) {
            diameter = maxDiameter;
        } else if (distance < maxDistance) {
            diameter = maxDiameter * (1 - ((distance - minDistance) / (maxDistance - minDistance)));
        }

        return Math.max(diameter, minDiameter);
    }

    private _highlightTripod(sphere: BABYLON.Mesh) {
        if (this._highlightedTripod === sphere) return;
        if (this._highlightedTripod) {
            this._unhighlightTripod(this._highlightedTripod);
        }

        let highlighted = false;
        if (sphere.material == this._scanMaterial) {
            if (sphere.material != this._scanHighlightMaterial) {
                sphere.material = this._scanHighlightMaterial;
                this._glowLayer.addIncludedOnlyMesh(sphere);
                highlighted = true;
            }
        }
        else if (sphere.material == this._blackAndWhiteScanMaterial) {
            sphere.material = this._blackAndWhiteScanHighlightMaterial;
            this._glowLayer.addIncludedOnlyMesh(sphere);
            highlighted = true;
        }
        else if (sphere.material == this._image360Material) {
            if (sphere.material != this._image360HighlightMaterial) {
                sphere.material = this._image360HighlightMaterial;
                this._glowLayer.addIncludedOnlyMesh(sphere);
                highlighted = true;
            }
        }

        this._highlightedTripod = highlighted ? sphere : null;
    }

    private _unhighlightTripod(sphere: BABYLON.Mesh) {
        if (sphere.material == this._scanHighlightMaterial) {
            sphere.material = this._scanMaterial;
            this._glowLayer.removeIncludedOnlyMesh(sphere);
        }
        else if (sphere.material == this._blackAndWhiteScanHighlightMaterial) {
            sphere.material = this._blackAndWhiteScanMaterial;
            this._glowLayer.removeIncludedOnlyMesh(sphere);
        }
        else if (sphere.material == this._image360HighlightMaterial) {
            sphere.material = this._image360Material;
            this._glowLayer.removeIncludedOnlyMesh(sphere);
        }

        if (this._highlightedTripod === sphere) {
            this._highlightedTripod = null;
        }
    }

    private _hasAnnotationChanged(annotation: IAnnotationDisplayItem, updatedAnnotation: IAnnotationDisplayItem): boolean {
        return annotation?.Text != updatedAnnotation?.Text ||
            annotation.Target2D?.X != updatedAnnotation.Target2D?.X ||
            annotation.Target2D?.Y != updatedAnnotation.Target2D?.Y ||
            annotation.Target3D?.X != updatedAnnotation.Target3D?.X ||
            annotation.Target3D?.Y != updatedAnnotation.Target3D?.Y ||
            annotation.Target3D?.Z != updatedAnnotation.Target3D?.Z ||
            annotation.TargetBoxMin?.X != updatedAnnotation.TargetBoxMin?.X ||
            annotation.TargetBoxMin?.Y != updatedAnnotation.TargetBoxMin?.Y ||
            annotation.TargetBoxMin?.Z != updatedAnnotation.TargetBoxMin?.Z ||
            annotation.TargetBoxMax?.X != updatedAnnotation.TargetBoxMax?.X ||
            annotation.TargetBoxMax?.Y != updatedAnnotation.TargetBoxMax?.Y ||
            annotation.TargetBoxMax?.Z != updatedAnnotation.TargetBoxMax?.Z ||
            annotation.AssetClass?.Name != updatedAnnotation.AssetClass?.Name;
    }

    private _deleteLabel(label: ILabel) {
        label.labelControl?.dispose();
        label.line?.dispose();
        label.attachedMesh?.dispose();
    }

    private _initializeAnnotations(annotations: IAnnotationDisplayItem[], selectedAnnotation: IAnnotationDisplayItem = null) {

        if (!this._guiTexture) {
            this._guiTexture = BABYLONGUI.AdvancedDynamicTexture.CreateFullscreenUI('UI');
        }

        // create a hashset for deleted annotations
        const deletedLabels = new Set(this._labels);
        const newLabelsList: ILabel[] = [];

        for (const annotation of annotations) {
            let labelData = this._labels.find(label => label.annotationData.ID == annotation.ID);

            if (labelData) {
                if (this._hasAnnotationChanged(labelData.annotationData, annotation)) {
                    labelData = this._createAnnotation(annotation, annotation.AssetClass.TagDisplayData);
                }
                else {
                    deletedLabels.delete(labelData);
                }
            }
            else {
                labelData = this._createAnnotation(annotation, annotation.AssetClass.TagDisplayData);
            }

            newLabelsList.push(labelData);

            if (annotation == selectedAnnotation) {
                this._selectLabel(labelData);
            }
        }

        if (!selectedAnnotation) {
            this._selectLabel(null);
        }

        this._labels = newLabelsList;

        // remove deleted labels
        deletedLabels.forEach(label => {
            this._deleteLabel(label);
        });

        // Precompute the layout of the annotations. This prevents a flicker when the annotations are first displayed.
        this._precomputeAnnotationLayouts();
    }

    private _precomputeAnnotationLayouts(container: BABYLONGUI.Container = this._guiTexture.rootContainer) {
        const textureSize = this._guiTexture.getSize();
        const context = this._guiTexture.getContext();
        const measure = new BABYLONGUI.Measure(0, 0, textureSize.width, textureSize.height);
        container._layout(measure, context);
    }

    private _initializeScene() {
        this._glowLayer = new BABYLON.GlowLayer('glow', this.scene);

        this._onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' || e.code === 'Escape') {
                this.events.dispatchEvent(new CustomEvent(PanoEvents.Viewer.LABEL_SELECTION, {
                    detail: { annotation: null }
                }));
            }
        };
        window.addEventListener('keydown', this._onKeyDown);

        this.scene.onPointerObservable.add((pointerInfo) => {
            if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERPICK) {
                this._handlePointerPick(pointerInfo);
            }
        });

        this.scene.onAfterCameraRenderObservable.add((camera) => this._onAfterCameraRender(camera));

        if (this._options.tagVolumeColor)
            this.boxManager.setBoxColor(this._options.tagVolumeColor);

        this.boxManager.events.addEventListener(PanoEvents.BoxManager.BOX_CHANGED, () => {
            if (this._selectedLabel) {
                const annotation = this._selectedLabel.annotationData;

                const box = this.boxManager.getBoxProperties();
                const halfScaleVec = new BABYLON.Vector3(box.xDim / 2, box.yDim / 2, box.zDim / 2);
                const boxMin = box.realWorldPos.subtract(halfScaleVec);
                const boxMax = box.realWorldPos.add(halfScaleVec);
                annotation.TargetBoxMin = { X: boxMin.x, Y: boxMin.y, Z: boxMin.z };
                annotation.TargetBoxMax = { X: boxMax.x, Y: boxMax.y, Z: boxMax.z };

                this._updateLabelPosition(this._selectedLabel, box.realWorldPos);

                this.events.dispatchEvent(new CustomEvent(PanoEvents.Viewer.ANNOTATION_UPDATE, {
                    detail: { annotation: annotation }
                }));
            }
        });
    }

    private _handlePointerPick(pointerInfo: BABYLON.PointerInfo) {

        // No picking while in measurement or volume manipulation modes
        if (this.viewer.displayMode != DisplayMode.CreateAnnotation && this.viewer.displayMode != DisplayMode.PositionAnnotation && this.viewer.displayMode != DisplayMode.Navigation)
            return;

        const canvasRect = this.scene.getEngine().getRenderingCanvasClientRect();
        const pointerX = pointerInfo.event.clientX - canvasRect.left;
        const pointerY = pointerInfo.event.clientY - canvasRect.top;

        // Create annotation mode picking logic
        if (this.viewer.displayMode == DisplayMode.CreateAnnotation || this.viewer.displayMode == DisplayMode.PositionAnnotation) {
            const worldCoord = this._viewer.convertScreenToLocalCoordinate(pointerX, pointerY);
            if (worldCoord) {
                const point3D: IVector3D = { X: worldCoord.x, Y: worldCoord.y, Z: worldCoord.z };
                const point2D: IVector2D = { X: pointerX, Y: pointerY };

                const annotation = this.viewer.displayMode == DisplayMode.PositionAnnotation ? this._selectedLabel?.annotationData : null;
                this.events.dispatchEvent(new CustomEvent(PanoEvents.Viewer.POINT3D_CLICKED, {
                    detail: { point3D: point3D, point2D: point2D, cameraPosition: this._selectedView.Position, annotationID: annotation?.ID }
                }));
            }

            return;
        }

        // Navigation mode picking logic
        if (this.viewer.displayMode == DisplayMode.Navigation) {

            // Picking order: labels, spheres, label box, (clear label selection if nothing was picked)

            // Pick labels
            const pickLabelsResult = this._handlePickLabels(pointerX, pointerY, pointerInfo);
            if (pickLabelsResult.labelPicked)
                return;

            // Pick navigation spheres (or label box)
            const pickResult = pointerInfo.pickInfo;
            if (pickResult?.hit && pickResult.pickedMesh) {
                const pickedView = this._getViewFromSphereMesh(pickResult.pickedMesh);
                if (pickedView) {
                    if (!this.isBusy) {
                        this.events.dispatchEvent(new CustomEvent(PanoEvents.Viewer.VIEW_CLICKED, {
                            detail: { view: pickedView, event: pointerInfo.event }
                        }));
                    }
                    return;
                }
                else if (pickResult.pickedMesh.name == BoxManager.BOX_NAME) {
                    // Do nothing if the box was picked, as the related annotation is already selected.
                    return;
                }
            }

            // Always clear selection if empty space is clicked
            if (pickLabelsResult.shallClearSelection) {
                this.events.dispatchEvent(new CustomEvent(PanoEvents.Viewer.LABEL_SELECTION, {
                    detail: { annotation: null }
                }));
            }

            return;
        }
    }

    private _handlePickLabels(pointerX: number, pointerY: number, pointerEvent: BABYLON.PointerInfo) {
        let hitRectangle = false;
        let labelIsSelected = false;

        // determine if any labels were picked
        for (var x = 0; x < this._labels.length; x++) {
            const label = this._labels[x];
            const annotationID = label.annotationData.ID;

            const isSelected = annotationID == this._selectedLabel?.annotationData.ID;
            labelIsSelected = labelIsSelected || isSelected;

            if (label.labelControl.contains(pointerX, pointerY)) {
                //if (!isSelected) {
                    this.events.dispatchEvent(new CustomEvent(PanoEvents.Viewer.LABEL_SELECTION, {
                        detail: {
                            annotationID: annotationID,
                            position: { x: label.labelControl.centerX, y: label.labelControl.centerY - label.labelControl.linkOffsetYInPixels },
                            pickPosition: { x: pointerEvent.event.clientX, y: pointerEvent.event.clientY },
                            isRightClick: pointerEvent.event.button === 2 ? true : false
                        },
                    }));
               // }

                hitRectangle = true;
                break;
            }
        }

        return { labelPicked: hitRectangle, shallClearSelection: (!hitRectangle) }
    }

    private async _initiateImageLoadAsync(imageUrl: string) {
        return new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = 'use-credentials';
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error(`Failed to load image at ${imageUrl}`));
            image.src = imageUrl;
        });
    }

    private async _loadDepthMap(selectedView: IView360Detail, onDepthReady?: () => void) {
        if (this._rotationValid) {
            const depthMap = new DepthMap(this.scene, selectedView.DepthMapUrl, selectedView.Position, selectedView.Width,
                selectedView.Height, selectedView.ScaleFactor, selectedView.Rotation, onDepthReady);

            this._depthMapInLoad = depthMap;
            try {
                await depthMap.loadDepthMap();

                this._depthMapInLoad = null;
                return depthMap;
            } catch (e) {
                return null;
            }
        }

        this._depthMapInLoad = null;
        return null;
    }

    private async _finishDepthMapLoad(loadDepthMapAsyncRequest: Promise<DepthMap>, onDepthReady?: () => void) {
        const depthMap = await loadDepthMapAsyncRequest;
        if (depthMap) {
            this._depthMap = depthMap;
            this._viewer.setDepthMap(depthMap);

            // Re-display the tripod spheres, this time with occlusion
            this._initializeTripods();
            onDepthReady?.()
        }
    }

    private _abortDepthMapLoad() {
        if (this._depthMapInLoad) {
            this._depthMapInLoad.abortLoad();
            this._depthMapInLoad = null;
        }
    }

    public async init(initialAnnotations: IAnnotationDisplayItem[], direction: BABYLON.Vector3, onViewerReady?: (viewer: PanoViewer) => void, onDepthReady?: () => void) {
        const selectedView = this._selectedView;

        // Load the pano image
        const image = await this._initiateImageLoadAsync(selectedView.ImageUrl);

        // Create the viewer
        this._viewer = new PanoViewer(this._container, image, selectedView.Rotation, direction as any, this._getPanoViewerOptions(selectedView));
        this._initializeScene();

        this._rotationValid = !!(
            selectedView?.Rotation &&
            selectedView.Rotation.X != null &&
            selectedView.Rotation.Y != null &&
            selectedView.Rotation.Z != null &&
            selectedView.Rotation.W != null
        );

        // Initiate the loading of the depth map
        const loadDepthMapAsyncRequest = this._loadDepthMap(selectedView, async () => {
            await this._finishDepthMapLoad(loadDepthMapAsyncRequest, onDepthReady);
            this.setMeasurements(ViewerStore.getMeasurements());
        });

        // Show the initial annotations
        this._initializeAnnotations(initialAnnotations);

        this._initialized = true;

        // Notify the caller that the viewer has been created
        onViewerReady?.(this._viewer);
    }

    public async changeSelectedView(selectedView: IView360Detail, initialAnnotations: IAnnotationDisplayItem[],
        cameraProperties?: IPanoCameraProperties, showTripods?: boolean, onViewChanged?: () => void, onDepthReady?: () => void) {
        if (this.isBusy) {
            return;
        }

        this._switchingViews = true;
        this._animationDone = false;
        this._options.showTripods = showTripods ?? false;

        // Cleanup
        this._abortDepthMapLoad();
        this._clearAnnotations();
        this._clearDepthMap();

        const imageUrl = selectedView.ImageUrl;

        // Initiate the loading of the new image
        const loadImageAsyncRequest = new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = 'use-credentials';
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error(`Failed to load image at ${imageUrl}`));
            image.src = imageUrl;
        });

        // Run the camera animation (if the viewer already exists)
        let cameraFOV = this.camera.fov;
        const curViewPosition = this._getVectorFromViewData(this._selectedView.Position);
        const newViewPosition = this._getVectorFromViewData(selectedView.Position);
        const directionToTarget = this._getHorizontalNavigationDirection(curViewPosition, newViewPosition);

        var nextViewRotationValid = !!(
            selectedView?.Rotation &&
            selectedView.Rotation.X != null &&
            selectedView.Rotation.Y != null &&
            selectedView.Rotation.Z != null &&
            selectedView.Rotation.W != null
        );

        const shouldAnimateCamera = nextViewRotationValid && this._rotationValid;

        const animateCameraAsyncRequest = shouldAnimateCamera
            ? this._viewer.changeCameraDirection(directionToTarget, cameraFOV * 0.6, true)
            : Promise.resolve();

        this._rotationValid = nextViewRotationValid;

        // Wait for the image to load
        const image = await loadImageAsyncRequest;

        // Initiate the loading of the depth map
        const loadDepthMapAsyncRequest = this._loadDepthMap(selectedView, async () => {
            while (!this._animationDone) {
                await this.sleep(200);
            }
            await this._finishDepthMapLoad(loadDepthMapAsyncRequest, onDepthReady);
        });

        // Wait for the camera animation to complete and then update the dome texture
        await animateCameraAsyncRequest;

        this._cleanupTripods(false);
        this._animationDone = true;
        this._viewer.setImageRotation(this._getPanoViewerOptions(selectedView).imageRotation);
        this._viewer.setViewerImage(image);

        if (cameraProperties) {
            if (cameraProperties.fov >= PanoViewer.MinZoomLevel && cameraProperties.fov <= PanoViewer.MaxZoomLevel) {
                cameraFOV = cameraProperties.fov;
            }

            this._viewer.setCameraProperties(cameraProperties.alpha, cameraProperties.beta);
        }

        this.camera.fov = cameraFOV;

        // Show the initial annotations
        this._selectedView = selectedView;
        this._initializeAnnotations(initialAnnotations);

        this._switchingViews = false;

        onViewChanged?.();
    }

    public updateOccludedAnnotations(annotations: IAnnotationDisplayItem[]) {
        if (!this._depthMap) {
            return annotations;
        }
        const cameraPosition = this._getVectorFromViewData(this._selectedView.Position);
        const occludedAnnotations = annotations.filter(ann => this._depthMap.isPositionOccluded(this._getVectorFromViewData(ann.Target3D), cameraPosition));

        this._occludedAnnotations = occludedAnnotations.map(ann => ann.ID);

        return occludedAnnotations;
    }

    public getScanSphereColor() {
        return this._options.scanSphereColor;
    }

    public getImageSphereColor() {
        return this._options.imageSphereColor;
    }

    public changeScanSphereColor(newColor: string) {
        if (this._options.scanSphereColor != newColor) {
            this._options.scanSphereColor = newColor ?? this.DefaultScanSphereColor;

            if (!this.isBusy) {
                this._initializeTripods(true);
            }
        }
    }

    public changeImageSphereColor(newColor: string) {
        if (this._options.imageSphereColor != newColor) {
            this._options.imageSphereColor = newColor ?? this.DefaultImageSphereColor;

            if (!this.isBusy) {
                this._initializeTripods(true);
            }
        }
    }

    public changeTagVolumeColor(newColor: string) {
        if (this._options.tagVolumeColor != newColor) {
            this._options.tagVolumeColor = newColor;
            this.boxManager?.setBoxColor(newColor);
        }
    }

    public getTripodsDisplay() {
        return this._options.showTripods;
    }

    public setTripodsDisplay(display: boolean) {
        if (this._options.showTripods != display) {
            this._options.showTripods = display;

            if (!this.isBusy) {
                this._initializeTripods(true);
            }
        }
    }

    public setScanPositionsDisplay(display: boolean) {
        if (this._options.showScanPositions != display) {
            this._options.showScanPositions = display;

            if (!this.isBusy) {
                this._initializeTripods(true);
            }
        }
    }

    public setColorScanPositionsDisplay(display: boolean) {
        if (this._options.showColorScanPositions != display) {
            this._options.showColorScanPositions = display;
            if (!this.isBusy) this._initializeTripods(true);
        }
    }

    public setBlackAndWhiteScanPositionsDisplay(display: boolean) {
        if (this._options.showBlackAndWhiteScanPositions != display) {
            this._options.showBlackAndWhiteScanPositions = display;
            if (!this.isBusy) this._initializeTripods(true);
        }
    }

    public setImagePositionsDisplay(display: boolean) {
        if (this._options.showImagePositions != display) {
            this._options.showImagePositions = display;

            if (!this.isBusy) {
                this._initializeTripods(true);
            }
        }
    }

    public getAnnotationsCount() {
        return this._labels.length;
    }

    public setAnnotations(annotations: IAnnotationDisplayItem[], selectedAnnotation: IAnnotationDisplayItem, readOnly: boolean) {

        this._annotationsReadOnly = readOnly;
        if (!this.isBusy) {
            // Restore source colors before changed labels are rebuilt and their
            // pre-override appearance cache is no longer available.
            this.resetAllAnnotationColorization();
            this._initializeAnnotations(annotations, selectedAnnotation);
        }
    }

    public highlightView(viewID: string) {
        const sphere = this._tripodsByViewId.get(viewID);
        if (sphere) {
            this._highlightTripod(sphere);
        }
    }

    public clearHighlightedView() {
        if (this._highlightedTripod) {
            this._unhighlightTripod(this._highlightedTripod);
        }
    }


    public alignToTarget(target: IVector3D) {
        const targetVector = this._getVectorFromViewData(target);
        const curViewPosition = this._getVectorFromViewData(this._selectedView.Position);
        const directionToTarget = targetVector.subtract(curViewPosition)
        directionToTarget.normalize();

        this.viewer.alignCamera(directionToTarget);
    }

    public setMeasurements(measurements: Measurement[]) {
        this._viewer?.setMeasurements(measurements);
    }

    public getViewerDirection() {
        return this._viewer?.cameraDirection;
    }

    private _getHorizontalNavigationDirection(from: BABYLON.Vector3, to: BABYLON.Vector3) {
        const direction = to.subtract(from);
        direction.y = 0;

        if (direction.lengthSquared() > 0.000001) {
            return direction.normalize();
        }

        const currentDirection = this._viewer?.cameraDirection?.clone();
        if (currentDirection) {
            currentDirection.y = 0;

            if (currentDirection.lengthSquared() > 0.000001) {
                return currentDirection.normalize();
            }
        }

        return new BABYLON.Vector3(0, 0, 1);
    }

    private sleep(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // ---------- Text color helpers (ES5-friendly) ----------
    private _setTextColorRecursive(root: BABYLONGUI.Control, color: string, cacheKey: string): void {
        if (!root) return;

        let cache = this._labelTextColorCache.get(cacheKey);
        if (!cache) {
            cache = new Map();
            this._labelTextColorCache.set(cacheKey, cache);
        }

        if (root instanceof BABYLONGUI.TextBlock) {
            if (!cache.has(root)) cache.set(root, root.color);
            (root as BABYLONGUI.TextBlock).color = color;
        }

        const anyCtrl: any = root as any;
        if (anyCtrl.children && Array.isArray(anyCtrl.children)) {
            for (let i = 0; i < anyCtrl.children.length; i++) {
                this._setTextColorRecursive(anyCtrl.children[i], color, cacheKey);
            }
        }
    }

    private _restoreTextColors(cacheKey: string): void {
        const cache = this._labelTextColorCache.get(cacheKey);
        if (!cache) return;

        cache.forEach((original, tb) => {
            try {
                if (tb && !(tb as any).isDisposed?.()) {
                    (tb as any).color = original;
                }
            } catch { }
        });

        this._labelTextColorCache.delete(cacheKey);
    }
}

export default PanoSceneManager;
